import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { onError, useInvalidateFollowUps } from "@/hooks/use-follow-up-mutations";
import type {
  DBFollowUp,
  FollowUpChannel,
  FollowUpOutcome,
  FollowUpPriority,
} from "@/lib/db-types";

/**
 * Lukking og utsettelse av oppfølginger (CRM-2.2). Begge går via RPC-ene
 * close_follow_up og postpone_follow_up, som håndhever «lukkes bare med utfall
 * og nytt neste steg» uansett klient. Audit skrives av DB-triggeren.
 */

export interface NextStepInput {
  title: string;
  due_date: string;
  priority: FollowUpPriority;
  channel?: FollowUpChannel | null;
  contact_id?: string | null;
}

export interface CloseFollowUpInput {
  followUp: Pick<DBFollowUp, "id" | "owner_id" | "customer_id" | "opportunity_id">;
  outcome: FollowUpOutcome;
  /** Notat ved telefon, grunn ved avslutning. */
  note?: string | null;
  /** Påkrevd for e-post og telefon. */
  next?: NextStepInput | null;
  /** Aktiviteten gmail-send skrev; påkrevd for e-post. */
  activityId?: string | null;
}

export function useCloseFollowUp() {
  const qc = useQueryClient();
  const invalidate = useInvalidateFollowUps();
  return useMutation({
    mutationFn: async ({ followUp, outcome, note, next, activityId }: CloseFollowUpInput) => {
      const trimmedNote = note?.trim() || null;
      const nextTitle = next?.title.trim();
      if (outcome !== "closed" && (!next || !nextTitle || !next.due_date)) {
        throw new Error("Neste steg (tittel og forfall) må fylles ut");
      }
      if (outcome === "closed" && !trimmedNote) throw new Error("Grunn må fylles ut");
      if (outcome === "email_sent" && !activityId) throw new Error("E-postaktiviteten mangler");

      const pNext =
        next && nextTitle && next.due_date
          ? {
              title: nextTitle,
              due_date: next.due_date,
              priority: next.priority,
              channel: next.channel ?? null,
              contact_id: next.contact_id ?? null,
              ...(outcome === "email_sent" ? { activity_id: activityId } : {}),
            }
          : outcome === "email_sent"
            ? { activity_id: activityId }
            : null;

      const { data, error } = await supabase.rpc("close_follow_up", {
        p_id: followUp.id,
        p_outcome: outcome,
        p_note: trimmedNote,
        p_next: pNext,
      });
      if (error) throw error;
      return data as { closed_id: string; next_id: string | null };
    },
    onSuccess: async (_result, { followUp, outcome }) => {
      await invalidate(followUp);
      if (outcome === "phone_done") {
        await invalidateFor(qc, "customer_activities", { customerId: followUp.customer_id });
        if (followUp.opportunity_id) {
          await invalidateFor(qc, "opportunities", {
            opportunityId: followUp.opportunity_id,
            customerId: followUp.customer_id,
          });
        }
      }
    },
    onError: onError("Kunne ikke lukke oppfølgingen"),
  });
}

export function usePostponeFollowUp() {
  const invalidate = useInvalidateFollowUps();
  return useMutation({
    mutationFn: async ({
      followUp,
      newDue,
      note,
    }: {
      followUp: Pick<DBFollowUp, "id" | "owner_id" | "customer_id">;
      newDue: string;
      note?: string | null;
    }) => {
      if (!newDue) throw new Error("Ny dato mangler");
      const { data, error } = await supabase.rpc("postpone_follow_up", {
        p_id: followUp.id,
        p_new_due: newDue,
        p_note: note?.trim() || null,
      });
      if (error) throw error;
      return data as DBFollowUp;
    },
    onSuccess: (_row, { followUp }) => invalidate(followUp),
    onError: onError("Kunne ikke utsette oppfølgingen"),
  });
}

